import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Field } from '@promtver/ui-kit';
import { BookingStatus } from '@promtver/contracts';

import { getPartnerBookings } from '../../api/partner';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal, formatTime, isWithinVisitWindow, todayInputValue } from '../../lib/datetime';
import type { PartnerGroupView } from '../../api/types';

function bookingStatusLabel(status: BookingStatus | string): string {
  switch (status) {
    case BookingStatus.PendingPayment:
      return ru.group.statusPendingPayment;
    case BookingStatus.Confirmed:
      return ru.group.statusConfirmed;
    case BookingStatus.Cancelled:
      return ru.group.statusCancelled;
    case BookingStatus.Completed:
      return ru.group.statusCompleted;
    default:
      return ru.common.notSpecified;
  }
}

/**
 * Список группы (FR-MGR-03): ФИО, количество мест, статус допуска, отметка
 * о прохождении ТБ, контакт для оперативной связи.
 *
 * Паспортные данные посетителя показываются ТОЛЬКО в период от брони
 * до окончания визита включительно (FR-DOC-04, Приложение В).
 */
export function GroupPage() {
  const [date, setDate] = useState(todayInputValue());
  const [now, setNow] = useState(() => Date.now());

  const bookingsQuery = useQuery({
    queryKey: ['partner', 'bookings', date],
    queryFn: () => getPartnerBookings(date),
  });

  const groups = bookingsQuery.data?.groups ?? [];

  return (
    <AppShell title={ru.group.title} subtitle={ru.group.subtitle} active="group">
      <Card title={ru.group.date}>
        <Field label={ru.group.date} value={date} onChange={setDate} type="date" required />
        <button
          type="button"
          onClick={() => setNow(Date.now())}
          className="min-h-touch self-start rounded-button border border-line px-4 text-sm font-semibold"
        >
          {ru.common.refresh}
        </button>
      </Card>

      <AsyncState
        isLoading={bookingsQuery.isLoading}
        isError={bookingsQuery.isError}
        isEmpty={groups.length === 0}
        emptyTitle={ru.group.noGroups}
        emptyDescription={ru.group.noGroupsDescription}
        onRetry={() => void bookingsQuery.refetch()}
      >
        <ul className="flex flex-col gap-4">
          {groups.map((group, index) => (
            <GroupCard key={group.booking.id} group={group} index={index} now={now} />
          ))}
        </ul>
      </AsyncState>
    </AppShell>
  );
}

function GroupCard({
  group,
  index,
  now,
}: {
  group: PartnerGroupView;
  index: number;
  now: number;
}) {
  const documentVisible = isWithinVisitWindow(
    group.booking.reservedAt,
    group.slot?.endsAt,
    new Date(now),
  );

  return (
    <Card
      title={`${ru.group.group} ${index + 1}`}
      action={
        <Badge tone={group.booking.status === BookingStatus.Confirmed ? 'success' : 'warning'}>
          {bookingStatusLabel(group.booking.status)}
        </Badge>
      }
    >
      <div className="flex flex-col gap-1">
        <KeyValue label={ru.group.places} value={group.booking.qty} mono />
        <KeyValue
          label={ru.group.slot}
          value={
            group.slot
              ? `${formatDateTimeLocal(group.slot.startsAt)} — ${formatTime(group.slot.endsAt)}`
              : ru.common.notSpecified
          }
        />
        <KeyValue
          label={ru.group.safety}
          value={
            group.safetyPassed === true
              ? ru.scanner.safetyPassed
              : group.safetyPassed === false
                ? ru.scanner.safetyFailed
                : ru.scanner.safetyUnknown
          }
        />
        <KeyValue
          label={ru.group.contact}
          value={
            group.contactPhone ?? group.guests.find((guest) => guest.phone)?.phone ?? ru.cabinet.contactPhoneMissing
          }
          mono
        />
      </div>

      <ul className="flex flex-col gap-2">
        {group.guests.map((guest, guestIndex) => (
          <li key={`${guest.fullName}-${guestIndex}`} className="rounded-card border border-line p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong className="text-sm">{guest.fullName}</strong>
              <Badge tone={guest.isCheckedIn ? 'success' : 'default'}>
                {guest.isCheckedIn ? ru.group.checkedIn : ru.group.notCheckedIn}
              </Badge>
            </div>

            {guest.phone ? (
              <div className="mt-1 text-xs text-muted">
                {ru.group.contact}: <span className="font-mono">{guest.phone}</span>
              </div>
            ) : null}

            <div className="mt-2">
              <span className="text-xs uppercase tracking-wide text-muted">{ru.group.document}</span>
              {documentVisible && guest.docSeries && guest.docNumber ? (
                <div className="font-mono text-sm">
                  {guest.docSeries} {guest.docNumber}
                </div>
              ) : (
                <div className="text-xs text-muted">
                  {ru.group.documentHidden}
                  <span className="block">{ru.group.documentHiddenHint}</span>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
